import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const RetryConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const strategies = [
    { value: 'fixed', label: 'Fix várakozás', icon: '⏱️', desc: 'Mindig ugyanannyi ideig vár' },
    { value: 'exponential', label: 'Exponenciális', icon: '📈', desc: 'Egyre hosszabb várakozás' },
    { value: 'linear', label: 'Lineáris', icon: '📊', desc: 'Lineárisan növekvő várakozás' },
  ];

  return (
    <div className="space-y-3">
      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Retry Beállítások"
        icon="🔄"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Maximum próbálkozások
            </label>
            <input
              type="number"
              value={node.data.config.maxRetries || 3}
              onChange={(e) => updateConfig('maxRetries', parseInt(e.target.value))}
              className={commonStyles.input}
              min="1"
              max="10"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Hányszor próbálja újra (1-10)
            </p>
          </div>

          <div>
            <label className={commonStyles.label}>
              Várakozási stratégia
            </label>
            <div className="grid grid-cols-1 gap-2">
              {strategies.map((strategy) => (
                <button
                  key={strategy.value}
                  onClick={() => updateConfig('strategy', strategy.value)}
                  className={`flex items-center p-3 rounded-md border text-sm transition-all ${
                    (node.data.config.strategy || 'fixed') === strategy.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-xl mr-3">{strategy.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{strategy.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{strategy.desc}</div>
                  </div>
                  {(node.data.config.strategy || 'fixed') === strategy.value && (
                    <span className="text-blue-500">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={commonStyles.label}>
              Várakozás (másodperc)
            </label>
            <input
              type="number"
              value={node.data.config.delaySeconds || 1}
              onChange={(e) => updateConfig('delaySeconds', parseInt(e.target.value))}
              className={commonStyles.input}
              min="0"
              max="60"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Várakozás újrapróbálkozások között
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="stopOnSuccess"
              checked={node.data.config.stopOnSuccess !== false}
              onChange={(e) => updateConfig('stopOnSuccess', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="stopOnSuccess" className="text-sm text-gray-700 dark:text-gray-300">
              Megállás sikeres végrehajtásnál
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{result}}', desc: 'Sikeres eredmény', output: 'success' },
              { var: '{{attempts}}', desc: 'Próbálkozások száma', output: 'both' },
              { var: '{{lastError}}', desc: 'Utolsó hiba', output: 'failed' },
              { var: '{{totalDuration}}', desc: 'Teljes időtartam (ms)', output: 'both' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.output}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🌐 API hívás retry:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Retry → HTTP Request → [success] Continue<br/>
              Retry → [failed] Error Notification
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🗄️ Database retry:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Retry → Database Query → [failed] Fallback
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default RetryConfig;
